import { MapContainer, TileLayer, Circle, Tooltip, useMap } from "react-leaflet";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { CATEGORY_LABELS, CATEGORY_ORDER, formatDate } from "@/lib/api";

function FitToEvenings({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) map.setView(points[0], 11);
    else map.fitBounds(L.latLngBounds(points).pad(0.3));
  }, [points, map]);
  return null;
}

const DATES = [["all", "Any date"], ["7", "Next 7 days"], ["30", "Next 30 days"]];
export default function DiscoveryMap({ evenings = [] }) {
  const [cat, setCat] = useState("all");
  const [dateRange, setDateRange] = useState("all");

  const located = useMemo(() => evenings.filter((e) => e.lat && e.lng), [evenings]);

  const filtered = useMemo(() => {
    const now = Date.now();
    return located.filter((e) => {
      if (cat !== "all" && e.category !== cat) return false;
      if (dateRange !== "all") {
        const days = parseInt(dateRange, 10);
        const t = new Date(e.date).getTime();
        if (t < now || t > now + days * 86400000) return false;
      }
      return true;
    });
  }, [located, cat, dateRange]);

  const points = useMemo(() => filtered.map((e) => [e.lat, e.lng]), [filtered]);

  return (
    <div data-testid="discovery-map" className="relative h-[480px] w-full overflow-hidden border border-gold/25 vivant-map">
      {/* Filter toolbar */}
      <div className="absolute top-3 left-3 right-3 z-[500] flex flex-wrap items-center justify-between gap-2">
        <select
          value={cat}
          onChange={(e) => setCat(e.target.value)}
          data-testid="map-category-filter"
          className="bg-velvet/88 backdrop-blur-sm border border-gold/25 text-parchment/90 text-[0.72rem] uppercase tracking-[0.12em] px-3 py-2 outline-none cursor-pointer"
        >
          <option value="all" className="bg-velvet text-parchment">All experiences</option>
          {CATEGORY_ORDER.map((c) => (
            <option key={c} value={c} className="bg-velvet text-parchment">{CATEGORY_LABELS[c]}</option>
          ))}
        </select>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          data-testid="map-date-filter"
          className="bg-velvet/88 backdrop-blur-sm border border-gold/25 text-parchment/90 text-[0.72rem] px-3 py-2 outline-none cursor-pointer"
        >
          {DATES.map(([v, l]) => <option key={v} value={v} className="bg-velvet text-parchment">{l}</option>)}
        </select>
      </div>

      <MapContainer
        center={[48.7, 2.3]}
        zoom={9}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%", background: "#EFE6D7" }}
        attributionControl={true}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; OpenStreetMap'
        />
        <FitToEvenings points={points} />
        {filtered.map((e) => (
          <Circle
            key={e.id}
            center={[e.lat, e.lng]}
            radius={e.sold_out ? 900 : 1500}
            pathOptions={{
              color: e.sold_out ? "#8A8178" : "#5B1A2B",
              fillColor: e.sold_out ? "#8A8178" : "#C89B3C",
              fillOpacity: 0.38,
              weight: 2,
            }}
          >
            <Tooltip direction="top" opacity={1} className="vivant-tooltip">
              <div style={{ minWidth: 160 }}>
                <div style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "#C89B3C" }}>
                  {CATEGORY_LABELS[e.category] || e.category} · {formatDate(e.date)}
                </div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 19, color: "#1E1A17", lineHeight: 1.1, marginTop: 2 }}>
                  {e.title}
                </div>
                <div style={{ fontSize: 11, color: "#5C544D", marginTop: 2 }}>{e.city}</div>
                <div style={{ fontSize: 11, color: e.sold_out ? "#8A8178" : "#5B1A2B", marginTop: 4, fontWeight: 600 }}>
                  {e.sold_out ? "Complet" : `${e.seats_available} seats left`}
                </div>
              </div>
            </Tooltip>
          </Circle>
        ))}
      </MapContainer>

      {/* clickable legend of filtered evenings */}
      <div className="absolute bottom-3 left-3 z-[500] bg-velvet/88 backdrop-blur-sm border border-gold/20 px-3 py-2 max-w-[70%]">
        <div className="text-[0.6rem] uppercase tracking-[0.2em] text-gold mb-1">
          {filtered.length} secret zone{filtered.length === 1 ? "" : "s"}
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
          {filtered.length === 0 ? (
            <span className="text-[0.72rem] text-parchment/60">No evenings match these filters.</span>
          ) : (
            filtered.slice(0, 6).map((e) => (
              <Link
                key={e.id}
                to={`/evenings/${e.id}`}
                data-testid={`map-zone-link-${e.id}`}
                className="text-[0.72rem] text-parchment/80 hover:text-gold transition-colors link-underline"
              >
                {e.city}
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
